import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { configuration } from "../../../configuration";
import { IssuePrivacyType, IssueStatus } from "../../../constants";
import { useSignalR } from "../../../hooks";
import { IUser } from "../../../models";
import { CreateIssueDto, FetchError, GetIssuesDto, IssueSummaryDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  newIssueGuid: string;
  teamName?: string;
  teamSlug?: string;
  createdByUser: IUser;
  description: string;
  note: string;
  contextActionItemGuid?: string;
  createdFromSurveyResponseAnswerGuid?: string;
  privacy: IssuePrivacyType;
};

const useCreateIssueRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createIssueRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateIssueDto = {
      issueGuid: variables.newIssueGuid,
      teamSlug: variables.teamSlug,
      description: variables.description,
      note: variables.note,
      contextActionItemGuid: variables.contextActionItemGuid,
      createdFromSurveyResponseAnswerGuid: variables.createdFromSurveyResponseAnswerGuid,
      privacy: variables.privacy,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createIssueRequest };
};

const useCreateIssue = () => {
  const { createIssueRequest } = useCreateIssueRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createIssueRequest,
    onMutate: async (variables) => {
      var newIssue: IssueSummaryDto = {
        guid: variables.newIssueGuid,
        assignedTeam:
          variables.teamSlug && variables.teamName ? { slug: variables.teamSlug, name: variables.teamName } : undefined,
        description: variables.description,
        note: variables.note,
        currentStatus: IssueStatus.Open,
        createdByUser: variables.createdByUser,
        createdDateTimeUtc: DateTime.now().toUTC().toISO({ includeOffset: false }),
        lastModifiedDateTimeUtc: DateTime.now().toUTC().toISO({ includeOffset: false }),
        upvotes: [],
        proposalCount: 0,
        commentCount: 0,
        userCanEditTitle: true,
        userCanEditOtherProperties: true,
        privacy: variables.privacy,
      };

      const cachedTeamData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(variables.teamSlug, undefined),
        (cachedData) => ({
          ...cachedData,
          issues: [...(cachedData.issues ?? []), newIssue],
        })
      );

      const cachedUserData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.createdByUser.userId),
        (cachedData) => ({
          ...cachedData,
          issues: [...(cachedData.issues ?? []), newIssue],
        })
      );

      return { cachedTeamData, cachedUserData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchUserIssues(variables.createdByUser.userId);

      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }

      if (variables.contextActionItemGuid) {
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
      }
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.issues.getIssues(variables.teamSlug, undefined), context?.cachedTeamData);
      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.createdByUser.userId),
        context?.cachedUserData
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUser.userId));

      // Update supporting items list
      if (variables.contextActionItemGuid) {
        queryClient.invalidateQueries(queryKeys.actionItems.getActionItemSummary(variables.contextActionItemGuid));
        queryClient.invalidateQueries(queryKeys.actionItems.getActionItemDetail(variables.contextActionItemGuid));
      }

      if (variables.createdFromSurveyResponseAnswerGuid) {
        queryClient.invalidateQueries(
          queryKeys.surveys.getItemsCreatedFromResponseAnswer(variables.createdFromSurveyResponseAnswerGuid)
        );
      }
    },
  });
};

export { useCreateIssue };
